import { useEffect, useRef, type MutableRefObject } from 'react';
import type {
  ChatIncomingMessage,
  ChatConnectionState,
  PublicIdentity,
  PublicSpace,
  PublicSpaceChannel,
  PublicSpaceChannelCategory,
  PublicSpaceMessage,
  SpaceNotifyLevel,
} from '@adieuu/shared';
import {
  handleSpaceSocketMessage,
  type SpaceAlertsContext,
  type SpaceChannelUnreadState,
  type SpaceSocketHandlerContext,
} from '../../services/spaceSocketHandlers';
import type { SpaceChannelMessagesState, SpacesContextValue } from './types';

export interface SpacesSocketEffectsParams {
  isLoggedIn: boolean;
  subscribe: (handler: (message: ChatIncomingMessage) => void) => () => void;
  onStateChange: (handler: (state: ChatConnectionState) => void) => () => void;
  setSpaces: React.Dispatch<React.SetStateAction<PublicSpace[]>>;
  setChannels: React.Dispatch<React.SetStateAction<PublicSpaceChannel[]>>;
  setCategories: React.Dispatch<React.SetStateAction<PublicSpaceChannelCategory[]>>;
  setMessagesByChannel: React.Dispatch<React.SetStateAction<Record<string, SpaceChannelMessagesState>>>;
  activeSpaceIdRef: MutableRefObject<string | null>;
  activeChannelIdRef: MutableRefObject<string | null>;
  identityIdRef: MutableRefObject<string | undefined>;
  /** Viewer's role IDs in the active Space, for local @role mention derivation. */
  activeSpaceRoleIdsRef: MutableRefObject<string[]>;
  /** Viewer's role IDs per joined Space, for @role mentions outside the active Space. */
  viewerRoleIdsBySpaceRef?: MutableRefObject<Record<string, string[]>>;
  /** Effective notification level per channel (see `SpaceSocketHandlerContext`). */
  resolveNotifyLevelRef?: MutableRefObject<
    (spaceId: string, channelId: string, categoryPath: readonly string[]) => SpaceNotifyLevel
  >;
  /** Announcement and Space-change alert routing (see `SpaceSocketHandlerContext`). */
  spaceAlertsRef?: MutableRefObject<SpaceAlertsContext>;
  refreshSpacesRef: MutableRefObject<() => Promise<void>>;
  refreshChannelMessagesRef: MutableRefObject<(spaceId: string, channelId: string) => void>;
  socketCallbacksRef: MutableRefObject<{
    onReactionAdded?: SpacesContextValue['onSocketReactionAdded'];
    onReactionRemoved?: SpacesContextValue['onSocketReactionRemoved'];
    onPinsUpdated?: SpacesContextValue['onSocketPinsUpdated'];
  }>;
  setUnreadByChannel: React.Dispatch<React.SetStateAction<Record<string, SpaceChannelUnreadState>>>;
  setUnreadBySpace: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  fireNotificationRef: MutableRefObject<
    | ((
        title: string,
        body: string,
        options?: {
          isMention?: boolean;
          isViewingChannel?: boolean;
          channelId?: string;
          spaceId?: string;
          spaceSlug?: string;
          onClick?: () => void;
        },
      ) => void)
    | undefined
  >;
  channelNamesRef: MutableRefObject<Record<string, string>>;
  canReadEncryptedMessageRef: MutableRefObject<(msg: PublicSpaceMessage) => boolean>;
  participantProfilesRef: MutableRefObject<Record<string, PublicIdentity>>;
  activeChannelMessagesRef: MutableRefObject<PublicSpaceMessage[]>;
  /** Called after local state is cleaned up for a deleted Space. */
  onSpaceDeleted?: (spaceId: string) => void;
  tRef?: MutableRefObject<(key: string, options?: Record<string, unknown>) => string>;
  setActiveSpace?: React.Dispatch<React.SetStateAction<PublicSpace | null>>;
  /** The viewer's own membership in a Space changed. */
  onViewerMemberUpdated?: (spaceId: string) => void;
}

export function useSpacesSocketEffects(params: SpacesSocketEffectsParams): void {
  const {
    isLoggedIn,
    subscribe,
    onStateChange,
    setSpaces,
    setChannels,
    setCategories,
    setMessagesByChannel,
    activeSpaceIdRef,
    activeChannelIdRef,
    identityIdRef,
    activeSpaceRoleIdsRef,
    viewerRoleIdsBySpaceRef,
    resolveNotifyLevelRef,
    spaceAlertsRef,
    refreshSpacesRef,
    refreshChannelMessagesRef,
    socketCallbacksRef,
    setUnreadByChannel,
    setUnreadBySpace,
    fireNotificationRef,
    channelNamesRef,
    canReadEncryptedMessageRef,
    participantProfilesRef,
    activeChannelMessagesRef,
    onSpaceDeleted,
    tRef,
    setActiveSpace,
    onViewerMemberUpdated,
  } = params;

  // Keep the delete callback on a ref so an inline parent identity cannot
  // re-run this effect. Re-subscribing calls onStateChange which immediately
  // emits the current state — and when that is `connected`, listMine storms.
  const onSpaceDeletedRef = useRef(onSpaceDeleted);
  onSpaceDeletedRef.current = onSpaceDeleted;
  const setActiveSpaceRef = useRef(setActiveSpace);
  setActiveSpaceRef.current = setActiveSpace;
  const onViewerMemberUpdatedRef = useRef(onViewerMemberUpdated);
  onViewerMemberUpdatedRef.current = onViewerMemberUpdated;

  useEffect(() => {
    if (!isLoggedIn) return;

    const unsubMessage = subscribe((message: ChatIncomingMessage) => {
      handleSpaceSocketMessage(message, {
        setSpaces: (updater) => setSpaces((prev) => updater(prev)),
        setChannels: (updater) => setChannels((prev) => updater(prev)),
        setCategories: (updater) => setCategories((prev) => updater(prev)),
        setMessagesByChannel: (updater) => setMessagesByChannel((prev) => updater(prev)),
        activeSpaceId: activeSpaceIdRef.current,
        activeChannelId: activeChannelIdRef.current,
        hasFocus: document.hasFocus(),
        identityId: identityIdRef.current,
        activeSpaceRoleIds: activeSpaceRoleIdsRef.current,
        viewerRoleIdsBySpace: viewerRoleIdsBySpaceRef?.current,
        resolveNotifyLevel: resolveNotifyLevelRef
          ? (spaceId, channelId, categoryPath) =>
              resolveNotifyLevelRef.current(spaceId, channelId, categoryPath)
          : undefined,
        ...spaceAlertsRef?.current,
        fetchChannelMessages: (spaceId, channelId) =>
          refreshChannelMessagesRef.current(spaceId, channelId),
        refreshSpaces: () => void refreshSpacesRef.current(),
        onSocketReactionAdded: (reaction) =>
          socketCallbacksRef.current.onReactionAdded?.(reaction),
        onSocketReactionRemoved: (messageId, reactionId, extra) =>
          socketCallbacksRef.current.onReactionRemoved?.(messageId, reactionId, extra),
        onSocketPinsUpdated: (messageId, action) =>
          socketCallbacksRef.current.onPinsUpdated?.(messageId, action),
        setUnreadByChannel: (updater) => setUnreadByChannel((prev) => updater(prev)),
        setUnreadBySpace: (updater) => setUnreadBySpace((prev) => updater(prev)),
        fireNotification: fireNotificationRef.current
          ? (title, body, opts) => fireNotificationRef.current?.(title, body, opts)
          : undefined,
        channelNames: channelNamesRef.current,
        canReadEncryptedMessage: (msg) => canReadEncryptedMessageRef.current(msg),
        participantProfiles: participantProfilesRef.current,
        activeChannelMessages: activeChannelMessagesRef.current,
        onSpaceDeleted: (spaceId) => onSpaceDeletedRef.current?.(spaceId),
        setActiveSpace: (updater) => setActiveSpaceRef.current?.((prev) => updater(prev)),
        onViewerMemberUpdated: (spaceId) => onViewerMemberUpdatedRef.current?.(spaceId),
        t: tRef?.current,
      });
    });

    const unsubState = onStateChange((state) => {
      if (state === 'connected') {
        void refreshSpacesRef.current();
        const spaceId = activeSpaceIdRef.current;
        const channelId = activeChannelIdRef.current;
        if (spaceId && channelId) {
          refreshChannelMessagesRef.current(spaceId, channelId);
        }
      }
    });

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const spaceId = activeSpaceIdRef.current;
      const channelId = activeChannelIdRef.current;
      if (spaceId && channelId) {
        refreshChannelMessagesRef.current(spaceId, channelId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);

    return () => {
      unsubMessage();
      unsubState();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [isLoggedIn, subscribe, onStateChange]);
}
